import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, ReplaySubject} from 'rxjs';
import {IziviaStorageService} from './storage/iziviaStorage.service';
import {IziviaMobileEnvironmentConfig} from '../../domain/environment/iziviaMobileEnvironmentConfig';
import {first, map, tap} from 'rxjs/operators';
import {ENV_CONFIG} from './storage/iziviaStorage.keys';
import {EnvironmentService} from './environment.service';
import { MatomoConfig } from '../../domain/analytics/matomoConfig';
import {IziviaThemeEnvironment} from '../../domain/environment/iziviaMobileEnvironment';

@Injectable({
    providedIn: 'root'
})
export class CurrentEnvironmentConfigService {

    private environmentConfigSubject: BehaviorSubject<IziviaMobileEnvironmentConfig> = new BehaviorSubject(undefined);
    private baseUrlSubject: ReplaySubject<string> = new ReplaySubject(1);
    private documentUrlSubject: ReplaySubject<string> = new ReplaySubject(1);
    private matomoConfigSubject: ReplaySubject<MatomoConfig | undefined> = new ReplaySubject(1);

    constructor(private readonly storageService: IziviaStorageService,
        private readonly environmentService: EnvironmentService) {
    }

    getCurrentValueEnvironmentConfig(): IziviaMobileEnvironmentConfig {
        return this.environmentConfigSubject.getValue();
    }

    getCurrentBaseUrl(): Observable<string> {
        return this.baseUrlSubject.asObservable().pipe(first());
    }

    getBaseUrl(): Observable<string> {
        return this.baseUrlSubject.asObservable();
    }

    getCurrentDocumentBaseUrl(): Observable<string> {
        return this.documentUrlSubject.asObservable().pipe(first());
    }

    getCurrentMatomoConfig(): Observable<MatomoConfig | undefined> {
        return this.matomoConfigSubject.asObservable().pipe(first());
    }

    getCurrentEnvironmentConfig(): Observable<IziviaMobileEnvironmentConfig> {
        return this.environmentConfigSubject.asObservable()
            .pipe(first(environmentConfig => !!environmentConfig));
    }

    setCurrentEnvironmentConfig(config: IziviaMobileEnvironmentConfig): Observable<IziviaMobileEnvironmentConfig> {
        return this.storageService.set(ENV_CONFIG, config)
            .pipe(
                tap(conf => this.environmentConfigSubject.next(conf)),
                tap(conf => this.computeEnvironmentProperties(conf))
            );
    }

    private computeEnvironmentProperties(config: IziviaMobileEnvironmentConfig): void {
        const environment: IziviaThemeEnvironment = this.environmentService.getEnvironmentFromKeyOrDefault(config.environmentKey);

        const currentUrl: string = environment.url || config.customUrl;
        this.baseUrlSubject.next(currentUrl);

        const documentUrl: string = environment.documentUrl || currentUrl;
        this.documentUrlSubject.next(documentUrl);

        this.matomoConfigSubject.next(environment.matomoConfig);
    }

    /**
     * Loads current environment config from storage or sets default
     *
     * Note: this method mist be run to inialize environment variables
     * therefore, it should be run on startup. Not doing so can cause problems like
     * the paymentLibrary not loading
     *
     */
    public loadCurrentEnvironmentConfig$(): Observable<IziviaMobileEnvironmentConfig> {
        return this.storageService.get<IziviaMobileEnvironmentConfig>(ENV_CONFIG).pipe(
            first(),
            map(config => {
                if (!config) {
                    const defaultEnvironment = this.environmentService.getDefaultEnvironment();
                    return {
                        environmentKey: defaultEnvironment.key,
                        customUrl: defaultEnvironment.url
                    };
                }

                return config;
            }),
            tap(config => {
                this.environmentConfigSubject.next(config);
                this.computeEnvironmentProperties(config);
            })
        );
    }
}
